import { $4, $id4 } from '@theme/utils';
import { ThemeEvents } from '@theme/events';
const  { cartUpdate, cartError } = ThemeEvents;

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends HTMLElement {
  #variantImage;
  #variantTitle;
  #variantPrice
  #variantButton;
  #controller;
  #controller2
  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #orderListObserver = null;
  #isStuck = false;
  #hiddenByBottom = false;
  #hiddenByOrderList = false;
  #buyButton2 ;
  connectedCallback() {
    this.#controller = new AbortController();
    this.#controller2 = new AbortController();
    const { signal } = this.#controller;
    this.#setupIntersectionObserver();
    this.#variantImage = $4('[ref="variantImage"]', this);
    this.#variantTitle = $4('[ref="variantTitle"]', this);
    this.#variantPrice = $4('[ref="variantPrice"]', this);
    this.#variantButton = $4('[ref="variantButton"]', this);

    this.#variantButton?.addEventListener('click', this.#handleAddToCartClick, { signal: this.#controller2.signal });


    document.addEventListener(cartUpdate, this.#handleCartAddComplete.bind(this, false), { signal });
    document.addEventListener(cartError, this.#handleCartAddComplete.bind(this, false), { signal });
    document.forms[this.dataset.form]?.addEventListener('product:updateInfo', this.#updateInfo, { signal });
  }

  disconnectedCallback() {
    //super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#orderListObserver?.disconnect();
    this.#controller.abort();
    this.#controller2.abort();
  }

  get #buyButton(){
    return this.#buyButton2 ??= document.forms[this.dataset.form]?.querySelector('[type="submit"]') || null;
  }
  get #buyButtonAboveViewport() {
    const rect = this.#buyButton.getBoundingClientRect();
    return rect.bottom < 0 || rect.top < 0;
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    if (!this.#buyButton) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = $id4('hdt-prefooter');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      this.#buyButton.inView = entry.isIntersecting;
      if (!entry.isIntersecting && !this.#isStuck && !this.#hiddenByOrderList) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        if (this.#buyButtonAboveViewport) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
          //console.log('Btn: show')
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hiddenByOrderList = false;
        this.#hideStickyBar();
        //console.log('Btn: hide')
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom && !this.#buyButton.inView) {
          // Footer out of view - check if we should show sticky bar again
          // Only show if buy buttons are above the viewport (scrolled past)
          if (this.#buyButtonAboveViewport) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(this.#buyButton);
    this.#mainBottomObserver.observe(footer);

    const quickOrderList = $4('quick-order-list-component');
    if (!quickOrderList) return;
    this.#orderListObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        this.#hiddenByOrderList = entry.isIntersecting;
        if (entry.isIntersecting && this.#isStuck) {
          //console.log('Quick: hide')
          //this.#hiddenByOrderList = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && !this.#buyButton.inView) {
            // Only show if buy buttons are above the viewport (scrolled past)
            if (this.#buyButtonAboveViewport) {
              //this.#hiddenByOrderList = false;
              //console.log('Quick: show')
              this.#showStickyBar();
            }
        }
      }
      // ,{
      //   rootMargin: '0px 0px 0px 0px',
      // }
    );
    // Observer for quick order list visibility - hides sticky bar
    this.#orderListObserver.observe(quickOrderList);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  #handleAddToCartClick = async () => {
    if (!this.#buyButton) return;
    this.#handleCartAddComplete(true);
    this.#buyButton.click();
  };
  #handleCartAddComplete(loading = false) {
    this.#variantButton.toggleAttribute('loading', loading);
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    this.#isStuck = true;
    this.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    this.#isStuck = false;
    this.dataset.stuck = 'false';
  }

  /**
   * Updates the button text to include quantity
   */
  #updateInfo = (event) => {
    const { sectionId, html } = event.detail,
    domFragment = $4(`#shopify-section-${sectionId}`, document.createRange().createContextualFragment(html)),
    stickyAddToCart = $4('hdt-sticky-add-to-cart', domFragment);
    const refs = ['variantImage','variantTitle','variantPrice','variantButton'];
    refs.forEach((ref) => {
      const newEl = $4(`[ref="${ref}"]`, stickyAddToCart);
      if (newEl) {
        if (ref === refs[0])      { this.#variantImage.replaceWith(newEl); this.#variantImage = newEl; }
        else if (ref === refs[1]) { this.#variantTitle.replaceWith(newEl); this.#variantTitle = newEl; }
        else if (ref === refs[2]) { this.#variantPrice.replaceWith(newEl); this.#variantPrice = newEl; }
        else if (ref === refs[3]) { this.#variantButton.replaceWith(newEl); this.#variantButton = newEl; }
      }
    });
    this.#controller2.abort();
    this.#buyButtonsIntersectionObserver.unobserve(this.#buyButton);
    requestAnimationFrame(() => {
      this.#controller2 = new AbortController();
      this.#variantButton?.addEventListener('click', this.#handleAddToCartClick, { signal: this.#controller2.signal });
      this.#buyButton2 = document.forms[this.dataset.form]?.querySelector('[type="submit"]') || null;
      this.#buyButtonsIntersectionObserver.observe(this.#buyButton);
    });
  }
}
customElements.define('hdt-sticky-add-to-cart', StickyAddToCartComponent);